/**
 * Authentication Context
 * Provides authentication state and actions throughout the application
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import {
  AuthContextType,
  AuthUser,
  AuthResponse,
  DomainValidationResponse,
} from '../types/auth.types';
import * as authService from '../services/auth.service';

// Create the context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider props
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Authentication Provider Component
 * Wraps the application to provide authentication state and actions
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

    /**
   * Initialize authentication on mount
   */
  useEffect(() => {
    const initAuth = async () => {
      try {
        setIsLoading(true);
        
        // Try to get current user from Supabase session
        const currentUser = await authService.getCurrentUser();
        
        if (currentUser) {
          setUser(currentUser);
          setIsAuthenticated(true);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        authService.removeStoredUser();
      } finally {
        setIsLoading(false);
      }
    };
    
    initAuth();
  }, []);

  /**
   * Login function
   */
  const login = useCallback(async (email: string, password: string): Promise<AuthResponse> => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await authService.login({ email, password });
      
      if (response.success && response.user) {
        setUser(response.user);
        setIsAuthenticated(true);
      } else {
        setError(response.error || 'Login failed');
      }
      
      return response;
    } catch (error: any) {
      const errorMessage = error.message || 'An unexpected error occurred';
      setError(errorMessage);
      
      return {
        success: false,
        error: errorMessage,
      };
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Register function
   */
  const register = useCallback(async (
    email: string,
    password: string,
    confirmPassword: string
  ): Promise<AuthResponse> => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await authService.register({ email, password, confirmPassword });
      
      if (response.success && response.user) {
        setUser(response.user);
        setIsAuthenticated(true);
      } else {
        setError(response.error || 'Registration failed');
      }
      
      return response;
    } catch (error: any) {
      const errorMessage = error.message || 'An unexpected error occurred';
      setError(errorMessage);
      
      return {
        success: false,
        error: errorMessage,
      };
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Logout function
   */
  const logout = useCallback(async () => {
    try {
      setIsLoading(true);
      await authService.logout();
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      setError(null);
      setIsLoading(false);
    }
  }, []);

  /**
   * Validate email domain against Supabase database
   */
  const validateDomain = useCallback(async (email: string): Promise<DomainValidationResponse> => {
    try {
      // Call the actual database validation from auth.service.ts
      const result = await authService.validateEmailDomain(email);
      
      if (result.valid && result.brokerId) {
        // Get broker details
        const brokerInfo = await authService.getBrokerInfo(result.brokerId);
        
        return {
          valid: true,
          broker: brokerInfo ? {
            id: brokerInfo.id,
            name: brokerInfo.name,
            website: brokerInfo.website || '',
          } : undefined,
          message: `Valid email for ${brokerInfo?.name || 'registered broker'}`,
        };
      }
      
      return {
        valid: false,
        message: result.error || 'Email domain must match a registered customs broker',
      };
    } catch (error: any) {
      return {
        valid: false,
        message: error.message || 'Domain validation failed',
      };
    }
  }, []);

  /**
   * Request password reset
   */
  const requestPasswordReset = useCallback(async (email: string): Promise<{ success: boolean; message: string }> => {
    try {
      setError(null);
      const result = await authService.requestPasswordReset(email);
      return {
        success: result.success,
        message: result.message || result.error || 'Unknown error',
      };
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to request password reset';
      setError(errorMessage);
      return {
        success: false,
        message: errorMessage,
      };
    }
  }, []);

  /**
   * Reset password
   */
  const resetPassword = useCallback(async (
    token: string,
    newPassword: string,
    confirmPassword: string
  ): Promise<{ success: boolean; message: string }> => {
    try {
      setError(null);
      const result = await authService.resetPassword(newPassword);
      return {
        success: result.success,
        message: result.message || result.error || 'Unknown error',
      };
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to reset password';
      setError(errorMessage);
      return {
        success: false,
        message: errorMessage,
      };
    }
  }, []);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Context value
  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    register,
    logout,
    validateDomain,
    requestPasswordReset,
    resetPassword,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Custom hook to use authentication context
 * @throws Error if used outside AuthProvider
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};

export default AuthContext;
